/**
 * Reports — Operating metrics (Reports Phase B / B.2, migrations 0151 + 0153).
 *
 * The durable, shareable core of the Monday Cowork operating review, native in
 * Peace OS — brought to parity with the founder's richer PDF operating
 * dashboard. Layout follows the PDF order: hero tiles (revenue w/ WoW, plan
 * verdict, cash, AR) → weekly revenue stacked → monthly line + table → CM
 * weekly → SPICE weekly → AR aging → collections → cash+AR combined →
 * expenses → clinician hours → quiet clients → the one-thing callout →
 * sources footnote.
 *
 * The richer surfaces ride the 0153 `sections` jsonb bag (payload v2). Each
 * section renders ONLY when present — an absent section hides its card and the
 * page degrades gracefully to the core weekly view. Derived surfaces (WoW,
 * verdict, CM/SPICE weekly stats, per-clinician stats, cash+AR combined) need
 * NO new data — they are computed from what is already on the wire.
 *
 * STANDING RULES honored here: plan-vs-actual is ALWAYS a BAND (calendar-day
 * and working-day proration bounds for the in-progress month) — never a
 * single-number verdict; and there are NO extrapolated projections. `status`
 * renders as a quiet chip (preliminary / restated).
 *
 * Owner-only: canViewReports gates the page (the read; board_observer reads
 * under 0152); canIngestOperatingMetrics gates the upload door (the write).
 * Charts are hand-rolled (div bars + inline-SVG polylines) — no dependency.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canViewReports, canIngestOperatingMetrics } from '@/lib/capabilities';
import {
  operatingMetricsApi,
  type OperatingMetricsReportWire,
  type WeeklyOperatingMetricWire,
  type OperatingMetricIngestResultWire,
} from '@/lib/api';
import { errorCodeOf, fieldOf, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { fieldSentence, metricsFieldLabel, METRICS_ERROR_FALLBACK } from '@/lib/fieldErrorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { PageShell } from '@/components/primitives/PageShell';
import { Button } from '@/components/primitives/Button';
import {
  money,
  moneyNum,
  hours,
  signedPct,
  formatWeekEnd,
  monthBand,
  verdictLabel,
  wowStats,
  monthlyAggregates,
} from '@/components/reports/operatingDerive';
import {
  WeeklyBarChart,
  MonthlyLineChart,
  CashArCombined,
  ArAgingCard,
  CollectionsCard,
  ExpensesCard,
  ClinicianHoursCard,
  TopClientsCard,
  SourcesFootnote,
} from '@/components/reports/OperatingSections';

const WEEKS = 12;

const FOREST = 'var(--color-sage-800, #1B4332)';
const GOLD = 'var(--color-warm-400, #C4A265)';

// ---------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------

function StatusChip({ status }: { status: WeeklyOperatingMetricWire['status'] }): ReactNode {
  if (status === 'preliminary') {
    return (
      <span
        data-testid="status-chip"
        data-status="preliminary"
        className="inline-flex items-center rounded-full bg-sage-800/[6%] px-2 py-0.5 text-[11px] font-medium text-ink-soft"
      >
        Preliminary
      </span>
    );
  }
  if (status === 'restated') {
    return (
      <span
        data-testid="status-chip"
        data-status="restated"
        className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700"
      >
        Restated
      </span>
    );
  }
  return (
    <span
      data-testid="status-chip"
      data-status="final"
      className="inline-flex items-center rounded-full bg-sage-800/[10%] px-2 py-0.5 text-[11px] font-medium text-sage-800"
    >
      Final
    </span>
  );
}

// ---------------------------------------------------------------------
// Headline cards
// ---------------------------------------------------------------------

function HeadlineCard({
  label,
  value,
  sub,
  children,
  testId,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  children?: ReactNode;
  testId: string;
}): ReactNode {
  return (
    <div
      data-testid={testId}
      className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4 flex flex-col gap-1"
    >
      <div className="text-[11.5px] font-semibold uppercase tracking-wider text-sage-400">{label}</div>
      <div className="font-display text-2xl text-sage-800 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-ink-soft">{sub}</div>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
// 12-week stacked CM/SPICE bars + hours line (the existing weekly chart)
// ---------------------------------------------------------------------

function RevenueTrend({ rows }: { rows: WeeklyOperatingMetricWire[] }): ReactNode {
  const weeks = [...rows].reverse();
  const maxRevenue = weeks.reduce((m, r) => {
    const cm = r.cm_revenue === null ? 0 : Number(r.cm_revenue);
    const sp = r.spice_revenue === null ? 0 : Number(r.spice_revenue);
    return Math.max(m, cm + sp);
  }, 0);
  const maxHours = weeks.reduce((m, r) => Math.max(m, r.total_hours === null ? 0 : Number(r.total_hours)), 0);

  const hoursPoints = weeks
    .map((r, i) => {
      if (r.total_hours === null || maxHours === 0) return null;
      const x = weeks.length === 1 ? 50 : (i / (weeks.length - 1)) * 100;
      const y = 100 - (Number(r.total_hours) / maxHours) * 100;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter((p): p is string => p !== null)
    .join(' ');

  return (
    <div data-testid="revenue-trend" className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-sage-800">Revenue by week</h2>
        <div className="flex items-center gap-3 text-[11px] text-ink-soft">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: FOREST }} /> CM
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: GOLD }} /> SPICE
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-3 rounded-full bg-amber-600" /> Hours
          </span>
        </div>
      </div>
      <div className="relative h-48">
        {hoursPoints && (
          <svg
            data-testid="hours-line"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full pointer-events-none"
          >
            <polyline points={hoursPoints} fill="none" stroke="#d97706" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
        <div className="flex items-end gap-1.5 h-full">
          {weeks.map((r) => {
            const cm = r.cm_revenue === null ? 0 : Number(r.cm_revenue);
            const sp = r.spice_revenue === null ? 0 : Number(r.spice_revenue);
            const cmPct = maxRevenue === 0 ? 0 : (cm / maxRevenue) * 100;
            const spPct = maxRevenue === 0 ? 0 : (sp / maxRevenue) * 100;
            return (
              <div
                key={r.week_ending}
                data-testid="revenue-bar"
                className="flex-1 h-full flex flex-col justify-end items-stretch"
                title={`Week ending ${formatWeekEnd(r.week_ending)}: CM ${money(r.cm_revenue)}, SPICE ${money(r.spice_revenue)}, ${hours(r.total_hours)}`}
              >
                <div className="w-full rounded-t-tight" style={{ height: `${spPct}%`, backgroundColor: GOLD }} />
                <div className="w-full" style={{ height: `${cmPct}%`, backgroundColor: FOREST }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {weeks.map((r) => (
          <div key={r.week_ending} className="flex-1 text-center text-[10px] leading-tight text-ink-soft">
            {formatWeekEnd(r.week_ending)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Monthly actual vs plan (band) vs prior year — the table (weekly-derived)
// ---------------------------------------------------------------------

function MonthlyTable({
  rows,
  monthly,
}: {
  rows: WeeklyOperatingMetricWire[];
  monthly?: Array<{ month: string; actual?: string | null; plan?: string | null; prior_year?: string | null }> | null;
}): ReactNode {
  const months = monthlyAggregates(rows, monthly);
  const currentMonthKey = months[0]?.key;
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <h2 className="font-display text-lg text-sage-800 mb-1">Monthly · actual vs plan vs prior year</h2>
      <p className="text-xs text-ink-soft mb-3">
        The current month&apos;s plan is a proration band (calendar-day and working-day bounds), never a single-number
        verdict.
      </p>
      <div className="overflow-x-auto">
        <table data-testid="monthly-table" className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
              <th className="py-2 pr-3">Month</th>
              <th className="py-2 px-3 text-right">Actual</th>
              <th className="py-2 px-3 text-right">Plan</th>
              <th className="py-2 pl-3 text-right">Prior year</th>
            </tr>
          </thead>
          <tbody>
            {months.map((mo) => {
              const isCurrent = mo.key === currentMonthKey;
              const band = isCurrent ? monthBand(rows) : null;
              return (
                <tr key={mo.key} className="border-b border-sage-800/[5%] last:border-0">
                  <td className="py-2 pr-3 text-ink">{mo.label}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-ink font-semibold">{moneyNum(mo.actual)}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-ink">
                    {isCurrent && band && band.hasPlan ? (
                      <span data-testid="plan-band" title="Calendar-day to working-day proration band">
                        {moneyNum(Math.round(band.bandLow!))} – {moneyNum(Math.round(band.bandHigh!))}
                      </span>
                    ) : mo.plan !== null ? (
                      moneyNum(mo.plan)
                    ) : (
                      '–'
                    )}
                  </td>
                  <td className="py-2 pl-3 text-right tabular-nums text-ink-soft">
                    {mo.priorYear === null ? '–' : moneyNum(mo.priorYear)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Upload door (owner-gated)
// ---------------------------------------------------------------------

const SAMPLE_PAYLOAD = `{
  "weeks": [
    {
      "week_ending": "2026-07-12",
      "total_revenue": "48250.00",
      "cm_revenue": "39100.00",
      "spice_revenue": "9150.00",
      "total_hours": "412.5",
      "clients_touched": 63,
      "ar_total": "128400.00",
      "ar_collectible_60plus": "96200.00",
      "ar_reserved": "12800.00",
      "cash": "215000.00",
      "plan_total": "50000.00",
      "plan_cm": "40000.00",
      "plan_spice": "10000.00",
      "crm_new_leads": 7,
      "crm_active_pipeline": 21,
      "crm_partner_touches": 14,
      "status": "final",
      "source_note": "QBO close w/e 7/12",
      "sections": {
        "monthly": [
          { "month": "2026-07", "actual": "94250.00", "plan": "100000.00", "prior_year": "88000.00" }
        ],
        "ar_aging": {
          "buckets": [
            { "bucket": "current", "collectible": "62000.00", "reserved": "0.00" },
            { "bucket": "31-60", "collectible": "34200.00", "reserved": "4000.00" },
            { "bucket": "91+", "collectible": "0.00", "reserved": "12800.00" }
          ],
          "note": "One 91+ account in dispute."
        },
        "collections": {
          "rows": [ { "client_name": "Margaret H.", "aged_amount": "8400.00", "total_on_book": "12800.00" } ],
          "note": "Two calls made this week."
        },
        "expenses": {
          "spend_this_week": "3100.00",
          "client_billable": "1900.00",
          "to_invoice_with_markup": "2052.00",
          "month_to_date": "9800.00",
          "note": "8% markup applied."
        },
        "clinician_hours": [
          { "name": "Ana R.", "role": "cm", "hours": "38.5" },
          { "name": "Dara P.", "role": "spice", "hours": "22.0" }
        ],
        "top_clients": [
          { "client_name": "Margaret H.", "services": ["cm"], "total_revenue": "6200.00", "avg_weekly_revenue": "520.00", "last_seen": "2026-07-11" },
          { "client_name": "Harold B.", "services": ["cm", "spice"], "total_revenue": "4800.00", "avg_weekly_revenue": "400.00", "last_seen": "2026-07-10" }
        ],
        "hours_note": "PTO across two CMs this week.",
        "cash_ar_note": "Cash dipped on payroll; AR steady.",
        "sources_note": "QBO + CareTree, w/e 2026-07-12."
      }
    }
  ]
}`;

/** A refused upload in one plain sentence: a field the label table can
 *  name, the paste as a whole, or the house sentence for anything else.
 *  The validator's words are never read. */
function humanizeApiError(err: unknown, contact: SupportContact): string {
  const code = errorCodeOf(err);
  if (code === 'invalid_value') {
    const f = fieldOf(err);
    if (f === 'week_ending') return fieldSentence('Week ending', { kind: 'sunday' });
    const label = f === null ? null : metricsFieldLabel(f);
    return label === null ? METRICS_ERROR_FALLBACK : fieldSentence(label, { kind: 'format' });
  }
  if (code === 'invalid_body') return METRICS_ERROR_FALLBACK;
  return pageWriteText(err, contact);
}

function UploadDoor({ tenantSlug }: { tenantSlug: string }): ReactNode {
  const qc = useQueryClient();
  const supportContact = useSupportContact();
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [results, setResults] = useState<OperatingMetricIngestResultWire[] | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => operatingMetricsApi.ingest(tenantSlug, payload),
    onSuccess: (data) => {
      setResults(data.results);
      setParseError(null);
      void qc.invalidateQueries({ queryKey: ['reports-operating-weekly', tenantSlug] });
    },
  });

  function onSubmit(): void {
    setResults(null);
    mutation.reset();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setParseError("That paste can't be read. Copy the whole block the Monday routine writes, and paste it again.");
      return;
    }
    setParseError(null);
    mutation.mutate(parsed as Record<string, unknown>);
  }

  return (
    <div data-testid="upload-door" className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <h2 className="font-display text-lg text-sage-800 mb-1">Upload weekly metrics</h2>
      <p className="text-xs text-ink-soft mb-3">
        Paste the JSON the Monday routine emits (one week, an array, or <code>{'{ weeks: [...] }'}</code>). Absent fields
        are preserved; an explicit <code>null</code> clears a stored value. The optional <code>sections</code> bag carries
        the richer dashboard (<code>monthly</code>, <code>ar_aging</code>, <code>collections</code>, <code>expenses</code>,{' '}
        <code>clinician_hours</code>, <code>top_clients</code>, and the note lines) and merges one key at a time (an absent
        section key is preserved; an explicit <code>null</code> clears just that key).
        Re-posting a changed <code>final</code> week (metrics OR sections) marks it <em>restated</em> automatically, and a
        restated week stays restated. <code>week_ending</code> must be a Sunday.
      </p>
      <details className="mb-3">
        <summary className="cursor-pointer text-xs font-medium text-sage-800">Show the expected format</summary>
        <pre
          data-testid="sample-payload"
          className="mt-2 overflow-x-auto rounded-tight bg-sage-800/[4%] p-3 text-[11px] leading-relaxed text-ink"
        >
          {SAMPLE_PAYLOAD}
        </pre>
      </details>
      <textarea
        data-testid="upload-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste weekly metrics JSON here…"
        rows={8}
        className="w-full rounded-tight border border-sage-800/[15%] bg-white p-3 font-mono text-xs text-ink focus:border-sage-800 focus:outline-none"
      />
      <div className="mt-3 flex items-center gap-3">
        <Button
          data-testid="upload-submit"
          type="button"
          variant="primary"
          size="sm"
          loading={mutation.isPending}
          disabled={text.trim() === ''}
          onClick={onSubmit}
        >
          {mutation.isPending ? 'Uploading…' : 'Upload'}
        </Button>
        {(parseError || mutation.isError) && (
          <span data-testid="upload-error" className="text-sm text-red-700">
            {parseError ?? humanizeApiError(mutation.error, supportContact)}
          </span>
        )}
      </div>
      {results && (
        <div data-testid="upload-results" className="mt-3 text-sm text-ink">
          <div className="font-medium text-sage-800">
            Saved {results.length} week{results.length === 1 ? '' : 's'}:
          </div>
          <ul className="mt-1 space-y-0.5">
            {results.map((r) => (
              <li key={r.week_ending} className="tabular-nums text-ink-soft">
                {formatWeekEnd(r.week_ending)} · <span className="font-medium text-ink">{r.outcome}</span> ({r.status})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Plan-verdict hero tile
// ---------------------------------------------------------------------

function VerdictTile({ rows }: { rows: WeeklyOperatingMetricWire[] }): ReactNode {
  const band = useMemo(() => monthBand(rows), [rows]);
  if (!band) return <HeadlineCard testId="card-verdict" label="Month vs plan" value="–" sub="no data" />;

  // Pace: MTD as a fraction of the calendar- and working-day prorated plan.
  const calPace = band.hasPlan && band.monthPlan > 0 ? (band.mtdActual / (band.monthPlan * band.calFrac)) * 100 : null;
  const workPace = band.hasPlan && band.monthPlan > 0 ? (band.mtdActual / (band.monthPlan * band.workFrac)) * 100 : null;

  return (
    <HeadlineCard
      testId="card-verdict"
      label={`${band.month} vs plan`}
      value={moneyNum(Math.round(band.mtdActual))}
      sub={
        band.hasPlan ? (
          <div className="space-y-0.5">
            <div className="font-medium text-ink">{verdictLabel(band.verdict)}</div>
            <div data-testid="headline-plan-band">
              plan band {moneyNum(Math.round(band.bandLow!))} – {moneyNum(Math.round(band.bandHigh!))}
            </div>
            {calPace !== null && workPace !== null && (
              <div className="text-[11px]">
                MTD {moneyNum(Math.round(band.mtdActual))} = {Math.round(calPace)}% of calendar pace, {Math.round(workPace)}% of working-day pace
              </div>
            )}
          </div>
        ) : (
          'no plan on file'
        )
      }
    />
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------

export function OperatingReports(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const allowed = canViewReports(whoami);
  const canIngest = canIngestOperatingMetrics(whoami);

  const query = useQuery<OperatingMetricsReportWire>({
    queryKey: ['reports-operating-weekly', tenantSlug, WEEKS],
    queryFn: ({ signal }) => operatingMetricsApi.weekly(tenantSlug, { weeks: WEEKS }, signal),
    staleTime: 60_000,
    enabled: allowed,
  });

  const data = query.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const isEmpty = !!data && rows.length === 0;
  const latest = rows[0];
  const wow = useMemo(() => wowStats(rows), [rows]);

  // The latest week's sections drive the richer cards (per-week bag). The
  // clinician-hours + cash/AR surfaces read ACROSS weeks; the rest read the
  // latest week. Absent -> the card hides (graceful degradation).
  const sections = latest?.sections ?? null;

  return (
    <PageShell
      testId="reports-operating-main"
      maxWidth="wide"
      eyebrow="Olive · Operations"
      title="Dashboard"
      access={{ allowed }}
      async={{ what: 'the reports',
        isLoading: allowed && query.isPending,
        isError: query.isError,
        error: query.error,
        onRetry: () => void query.refetch(),
        isEmpty: isEmpty && !canIngest,
        emptyHeadline: "Once the Monday routine posts a week, you'll see its metrics here.",
      }}
    >
      <div className="space-y-6">
        {latest && (
          <>
            <div className="flex items-center gap-2 text-sm text-ink-soft">
              <span>
                Latest week ending <span className="font-medium text-ink">{formatWeekEnd(latest.week_ending)}</span>
              </span>
              <StatusChip status={latest.status} />
              {latest.source_note && <span className="text-xs italic">· {latest.source_note}</span>}
            </div>

            {/* Hero tiles: revenue (w/ WoW), plan verdict, cash, AR */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <HeadlineCard
                testId="card-revenue"
                label="Last week revenue"
                value={money(latest.total_revenue)}
                sub={`CM ${money(latest.cm_revenue)} · SPICE ${money(latest.spice_revenue)}`}
              >
                <div data-testid="revenue-wow" className="text-[11px] text-ink-soft mt-0.5">
                  WoW {signedPct(wow.wowPct)} · vs 12-wk avg {signedPct(wow.vs12wkPct)}
                </div>
              </HeadlineCard>
              <VerdictTile rows={rows} />
              <HeadlineCard
                testId="card-cash"
                label="Cash"
                value={money(latest.cash)}
                sub={
                  wow.cashDelta === null
                    ? 'at the Sunday close'
                    : `${wow.cashDelta >= 0 ? '+' : ''}${moneyNum(wow.cashDelta)} vs prior week`
                }
              />
              <HeadlineCard
                testId="card-ar"
                label="AR"
                value={money(latest.ar_total)}
                sub={`collectible ${money(latest.ar_collectible_60plus)} · reserved ${money(latest.ar_reserved)}`}
              />
            </div>

            {/* Weekly revenue stacked */}
            <RevenueTrend rows={rows} />

            {/* Monthly line (sections) + the weekly-derived table */}
            {sections?.monthly && sections.monthly.length > 0 && (
              <MonthlyLineChart monthly={sections.monthly} testId="monthly-line" />
            )}
            <MonthlyTable rows={rows} monthly={sections?.monthly ?? null} />

            {/* CM weekly + SPICE weekly (derived from core columns) */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <WeeklyBarChart title="CM revenue · weekly" rows={rows} pick={(r) => r.cm_revenue} color={FOREST} testId="cm-weekly" />
              <WeeklyBarChart title="SPICE revenue · weekly" rows={rows} pick={(r) => r.spice_revenue} color={GOLD} testId="spice-weekly" />
            </div>

            {/* AR aging + collections (sections) */}
            {sections?.ar_aging && <ArAgingCard arAging={sections.ar_aging} testId="ar-aging" />}
            {sections?.collections && <CollectionsCard collections={sections.collections} testId="collections" />}

            {/* Combined cash + AR (derived) */}
            <CashArCombined rows={rows} note={sections?.cash_ar_note ?? null} testId="cash-ar-combined" />

            {/* Expenses (sections) */}
            {sections?.expenses && <ExpensesCard expenses={sections.expenses} testId="expenses" />}

            {/* Clinician hours across weeks (sections.clinician_hours per week);
                CM Utilization + hours flag use roster targets from Kith. */}
            <ClinicianHoursCard
              rows={rows}
              note={sections?.hours_note ?? null}
              rosterTargets={data?.roster_targets ?? []}
              testId="clinician-hours"
            />

            {/* Top revenue clients (sections.top_clients) — replaces quiet clients */}
            {sections?.top_clients && sections.top_clients.length > 0 && (
              <TopClientsCard topClients={sections.top_clients} testId="top-clients" />
            )}

            {/* Sources footnote (sections.sources_note) */}
            {sections?.sources_note && <SourcesFootnote note={sections.sources_note} testId="sources-note" />}
          </>
        )}

        {canIngest && <UploadDoor tenantSlug={tenantSlug} />}
      </div>
    </PageShell>
  );
}
